// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson
// This file is part of Hushold.
//
// Hushold is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as
// published by the Free Software Foundation, version 3.

// Browser-side investment utilities — mirrors packages/core/src/investment.ts
// but imports from webapp types (same logic, no Node deps).

import type { Transaction, Price, Account } from '../types.js';

export const SHARE = 100000; // 1 share = 100000 hundred-thousandths

export interface CostBasisLot {
  purchaseDate: string;
  shares: number;          // hundred-thousandths
  costPerShare: number;    // cents per 1 share
  totalCost: number;       // cents
  remainingShares: number; // hundred-thousandths
}

export interface RealizedGainLot {
  lot: CostBasisLot;
  sharesConsumed: number;
  gainLoss: number;
  term: 'short' | 'long';
}

export interface RealizedGainResult {
  gainLoss: number;
  isShortTerm: boolean;
  isLongTerm: boolean;
  lotsConsumed: RealizedGainLot[];
}

export type InvestmentAction = 'buy' | 'sell' | 'div' | 'rdiv' | 'split' | 'other';

export const INVESTMENT_ACCOUNT_TYPES = new Set(['STOCK', 'MUTUAL_FUND', 'INVESTMENT']);

export function isInvestmentAccount(account: Account): boolean {
  return INVESTMENT_ACCOUNT_TYPES.has(account.type);
}

function isLongTermHold(purchaseDate: string, saleDate: string): boolean {
  const purchase = new Date(purchaseDate + 'T00:00:00');
  const sale = new Date(saleDate + 'T00:00:00');
  const oneYearAfter = new Date(purchase);
  oneYearAfter.setFullYear(oneYearAfter.getFullYear() + 1);
  return sale >= oneYearAfter;
}

export function calculateCostBasis(accountId: string, transactions: Transaction[]): CostBasisLot[] {
  const sorted = [...transactions]
    .filter((tx) => !tx.voided && tx.splits.some((s) => s.accountId === accountId))
    .sort((a, b) =>
      a.date !== b.date ? a.date.localeCompare(b.date) : a.dateEntered.localeCompare(b.dateEntered),
    );

  const lots: CostBasisLot[] = [];

  for (const tx of sorted) {
    const split = tx.splits.find((s) => s.accountId === accountId);
    if (!split) continue;

    if (split.quantity > 0) {
      const costPerShare = split.quantity > 0
        ? Math.round((split.amount * SHARE) / split.quantity)
        : 0;
      lots.push({
        purchaseDate: tx.date,
        shares: split.quantity,
        costPerShare,
        totalCost: split.amount,
        remainingShares: split.quantity,
      });
    } else if (split.quantity < 0) {
      let toConsume = -split.quantity;
      for (const lot of lots) {
        if (toConsume <= 0) break;
        const consumed = Math.min(lot.remainingShares, toConsume);
        lot.remainingShares -= consumed;
        toConsume -= consumed;
      }
    }
  }

  return lots;
}

export function calculateRealizedGainFromProceeds(
  costBasisLots: CostBasisLot[],
  sharesToSell: number,
  grossProceeds: number,
  saleDate: string,
): RealizedGainResult {
  const lotsConsumed: RealizedGainLot[] = [];
  let remaining = sharesToSell;
  let totalBasis = 0;

  const workingLots = costBasisLots.map((l) => ({ ...l }));

  for (const lot of workingLots) {
    if (remaining <= 0) break;
    if (lot.remainingShares <= 0) continue;
    const consumed = Math.min(lot.remainingShares, remaining);
    const basisForConsumed = Math.round((consumed * lot.costPerShare) / SHARE);
    const term = isLongTermHold(lot.purchaseDate, saleDate) ? 'long' : 'short';
    lotsConsumed.push({ lot, sharesConsumed: consumed, gainLoss: 0, term });
    totalBasis += basisForConsumed;
    remaining -= consumed;
  }

  const totalGain = grossProceeds - totalBasis;

  for (let i = 0; i < lotsConsumed.length; i++) {
    const item = lotsConsumed[i]!;
    const lotBasis = Math.round((item.sharesConsumed * item.lot.costPerShare) / SHARE);
    const lotProceeds =
      i === lotsConsumed.length - 1
        ? grossProceeds - (totalBasis - lotBasis)
        : Math.round((lotBasis / Math.max(totalBasis, 1)) * grossProceeds);
    item.gainLoss = lotProceeds - lotBasis;
  }

  return {
    gainLoss: totalGain,
    isShortTerm: lotsConsumed.some((l) => l.term === 'short'),
    isLongTerm: lotsConsumed.some((l) => l.term === 'long'),
    lotsConsumed,
  };
}

export function calculateMarketValue(shares: number, pricePerShare: number): number {
  return Math.round((shares * pricePerShare) / SHARE);
}

export function calculateUnrealizedGain(
  currentValue: number,
  costBasis: number,
): { amount: number; percentage: number } {
  const amount = currentValue - costBasis;
  return { amount, percentage: costBasis !== 0 ? amount / costBasis : 0 };
}

export function getLatestPrice(
  commodityId: string,
  asOfDate: string,
  prices: Price[],
): Price | null {
  const candidates = prices
    .filter((p) => p.commodityId === commodityId && p.date <= asOfDate)
    .sort((a, b) => b.date.localeCompare(a.date));
  return candidates[0] ?? null;
}

export function calculateSharesHeld(
  accountId: string,
  transactions: Transaction[],
  asOfDate?: string,
): number {
  let total = 0;
  for (const tx of transactions) {
    if (tx.voided) continue;
    if (asOfDate && tx.date > asOfDate) continue;
    const split = tx.splits.find((s) => s.accountId === accountId);
    if (split) total += split.quantity;
  }
  return total;
}

export function calculateAverageCostBasis(accountId: string, transactions: Transaction[]): number {
  let totalCost = 0;
  let totalShares = 0;

  const sorted = [...transactions]
    .filter((tx) => !tx.voided && tx.splits.some((s) => s.accountId === accountId))
    .sort((a, b) =>
      a.date !== b.date ? a.date.localeCompare(b.date) : a.dateEntered.localeCompare(b.dateEntered),
    );

  for (const tx of sorted) {
    const split = tx.splits.find((s) => s.accountId === accountId);
    if (!split) continue;
    if (split.quantity > 0) {
      totalCost += split.amount;
      totalShares += split.quantity;
    } else if (split.quantity < 0 && totalShares > 0) {
      const avgCost = totalCost / totalShares;
      const soldShares = -split.quantity;
      totalCost = Math.max(0, totalCost - Math.round(avgCost * soldShares));
      totalShares = Math.max(0, totalShares - soldShares);
    }
  }

  return totalShares === 0 ? 0 : Math.round((totalCost * SHARE) / totalShares);
}

export function calculateTotalCostBasis(lots: CostBasisLot[]): number {
  return lots.reduce((sum, lot) => {
    if (lot.remainingShares <= 0) return sum;
    return sum + Math.round((lot.remainingShares * lot.costPerShare) / SHARE);
  }, 0);
}

export function getInvestmentAction(tx: Transaction, investmentAccountId: string): InvestmentAction {
  const invSplit = tx.splits.find((s) => s.accountId === investmentAccountId);
  if (!invSplit) return 'other';

  const action = invSplit.action?.toLowerCase() ?? '';
  if (action === 'buy' || action === 'buyx') return 'buy';
  if (action === 'sell' || action === 'sellx') return 'sell';
  if (action === 'div') return 'div';
  if (action === 'reinvdiv' || action === 'rdiv') return 'rdiv';
  if (action === 'split' || action === 'stksplit') return 'split';

  if (invSplit.quantity > 0 && invSplit.amount > 0) return 'buy';
  if (invSplit.quantity < 0 && invSplit.amount < 0) return 'sell';
  if (invSplit.quantity === 0 && invSplit.amount > 0) return 'div';
  if (invSplit.quantity > 0 && invSplit.amount === 0) return 'rdiv';
  return 'other';
}

// Get the commodity identifier for an investment account — we use the ticker as commodityId.
export function getCommodityId(account: Account): string {
  return account.commodity?.ticker ?? account.id;
}

// Format shares (stored in hundred-thousandths) for display.
export function formatShares(hundredThousandths: number, decimals = 3): string {
  return (hundredThousandths / SHARE).toFixed(decimals);
}

// Parse a decimal share string to hundred-thousandths.
export function parseShares(str: string): number {
  const n = parseFloat(str);
  if (isNaN(n)) return 0;
  return Math.round(n * SHARE);
}

// Format a price in cents as a decimal dollar string.
export function formatPriceInput(cents: number): string {
  return (cents / 100).toFixed(2);
}

// Parse a dollar string to cents.
export function parsePrice(str: string): number {
  const n = parseFloat(str);
  if (isNaN(n)) return 0;
  return Math.round(n * 100);
}
